import { useState } from "react";
import { toast } from "react-toastify";
import { FaHouseUser } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { PropagateLoader } from "react-spinners";
import DataTable from "react-data-table-component";
import { FaBuildingShield } from "react-icons/fa6";
import IconButton from "../../atoms/IconButton";
import NoData from "../../molecules/NoData";
import Modal from "../../molecules/Modal";
import ModalConfirm from "../../molecules/ModalConfirm";
import ActionsTables from "../../molecules/ActionsTables";
import RentEdit from "./services/RentEdit";
import InmueblesEdit from "./InmueblesEdit";
import HipotecaEdit from "./services/HipotecaEdit";
import { DELETE_INMUEBLE } from "../../../api/apiInmuebles";
import useModal from "../../../hooks/useModal";
import { getConditionalRowStyles } from "../../../utils/rowStyles";

const InmueblesTable = ({ data, refetch, loading }) => {
    const navigate = useNavigate();
    const { open, isOpen, close } = useModal();
    const [selected, setSelected] = useState(null);
    const [expandedRow, setExpandedRow] = useState(null);
    const [loadingDelete, setLoadingDelete] = useState();
    const { open: openRent, isOpen: isOpenRent, close: closeRent } = useModal();
    const { open: openDelete, isOpen: isOpenDelete, close: closeDelete } = useModal();
    const { open: openHipoteca, isOpen: isOpenHipoteca, close: closeHipoteca } = useModal();

    const handleOpenEdit = (inmueble) => {
        open();
        setSelected(inmueble);
    };

    const handleOpenRent = (inmueble) => {
        openRent();
        setSelected(inmueble);
    };

    const handleOpenHipoteca = (inmueble) => {
        openHipoteca();
        setSelected(inmueble);
    };

    const handleOpenDelete = (inmueble) => {
        openDelete();
        setSelected(inmueble);
    };

    const handleClose = () => {
        close();
        closeRent();
        closeDelete();
        closeHipoteca();
        setSelected(null);
    };

    const handleDelete = () => {
        setLoadingDelete(true);
        toast.promise(
            DELETE_INMUEBLE(selected.id),
            {
                pending: 'Eliminando inmueble...',
                success: 'Inmueble eliminado correctamente.',
                error: 'No se pudo eliminar el inmueble.'
            }
        ).then(() => {
            refetch();
            handleClose();
        }).catch((error) => {
            console.error(error);
        }).finally(() => {
            setLoadingDelete(false);
        });
    };

    const columns = [
        {
            name: "Inmueble",
            cell: (row) => (
                <div className="flex flex-col cursor-pointer" onClick={() => handleRowClick(row)}>
                    <p>
                        <strong> {row.name} </strong>
                        ({row.type})
                    </p>
                    <p> {row.address} </p>
                </div>
            ),
        },
        {
            name: "Renta",
            selector: row => row.isRented,
            cell: (row) => (
                <div onClick={() => handleOpenRent(row)}>
                    <p className={`${row.isRented ? "bg-green-500" : "bg-red-500"} rounded-md p-1.5 text-white font-bold`}> {row.isRented ? "Rentado" : "No rentado"} </p>
                </div>
            ),
            sortable: true,
        },
        {
            name: "Hipoteca",
            selector: row => row.isMortgaged,
            cell: (row) => (
                <div onClick={() => handleOpenHipoteca(row)}>
                    <p className={`${row.isMortgaged ? "bg-green-500" : "bg-red-500"} rounded-md p-1.5 text-white font-bold`}> {row.isMortgaged ? "Hipotecado" : "No hipotecado"} </p>
                </div>
            ),
            sortable: true,
        },
        {
            name: "Acciones",
            cell: (row) => (<ActionsTables row={row} handleOpenEdit={handleOpenEdit} handleOpenDelete={handleOpenDelete} />),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];

    const columnsResponsive = [
        {
            name: "Inmueble",
            cell: (row) => (
                <div className="flex flex-col">
                    <p>
                        <strong> {row.name} </strong>
                        ({row.type})
                    </p>
                    <p> {row.address} </p>
                    <p className={`${row.isRented ? "text-green-500" : "text-red-500"} font-bold`}> {row.isRented ? "Rentado" : "No rentado"} </p>
                    <p className={`${row.isMortgaged ? "text-green-500" : "text-red-500"} font-bold`}> {row.isMortgaged ? "Hipotecado" : "No hipotecado"} </p>
                </div>
            )
        },
    ];

    const handleRowClick = (row) => {
        navigate(`${row.id}`);
    };

    const conditionalRowStyles = getConditionalRowStyles(expandedRow);

    const ExpandedComponent = ({ data }) => (
        <div className="p-2 w-full flex flex-row gap-2 justify-around">
            <div className="flex flex-row gap-2">
                <IconButton icon={FaBuildingShield} onClick={() => handleOpenHipoteca(data)} bgColor={data.isMortgaged ? "bg-green-500" : "bg-red-500"} />
                <IconButton icon={FaHouseUser} onClick={() => handleOpenRent(data)} bgColor={data.isRented ? "bg-green-500" : "bg-red-500"} />
            </div>
            <ActionsTables row={data} handleOpenEdit={handleOpenEdit} handleOpenDelete={handleOpenDelete} />
        </div>
    );

    return (
        <>
            <div className="block md:hidden">
                <DataTable
                    data={data || []}
                    progressPending={loading}
                    columns={columnsResponsive}
                    progressComponent={<div className="w-full h-20 flex justify-center items-center"> <PropagateLoader color="red" /> </div>}
                    noDataComponent={<NoData />}
                    pagination
                    onRowClicked={handleRowClick}
                    expandableRows
                    expandableRowsComponent={ExpandedComponent}
                    expandableRowExpanded={(row) => row?.id === expandedRow?.id}
                    onRowExpandToggled={(expanded, row) => {
                        setExpandedRow(expanded ? row : null);
                    }}
                    conditionalRowStyles={conditionalRowStyles}
                />
            </div>
            <div className="hidden md:block">
                <DataTable
                    data={data}
                    progressPending={loading}
                    columns={columns}
                    progressComponent={<div className="w-full h-20 flex justify-center items-center"> <PropagateLoader color="red" /> </div>}
                    noDataComponent={<NoData />}
                    pagination
                    onRowClicked={handleRowClick}
                    highlightOnHover
                    pointerOnHover
                />
            </div>
            {isOpen && selected &&
                <Modal title="Editar inmueble" onClose={handleClose} heigth="h-[75vh] md:h-[50vh]">
                    <InmueblesEdit inmueble={selected} refetch={refetch} onClose={handleClose} />
                </Modal>
            }
            {isOpenRent && selected &&
                <Modal title={`Rentar - ${selected?.name}`} onClose={handleClose}>
                    <RentEdit inmueble={selected} refetch={refetch} onClose={handleClose} />
                </Modal>
            }
            {isOpenHipoteca && selected &&
                <Modal title="Hipotecar" onClose={handleClose}>
                    <HipotecaEdit inmueble={selected} refetch={refetch} onClose={handleClose} />
                </Modal>
            }
            {isOpenDelete && selected && <ModalConfirm loading={loadingDelete} onConfirm={handleDelete} onClose={handleClose} eliminacion />}
        </>
    );
};

export default InmueblesTable;
